<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, shallowRef } from 'vue';
import { inputGroupAffixKey } from '../form-input-group/affixContext';
import IdeoInputGroupText from '../form-input-group/IdeoInputGroupText.vue';

const props = withDefaults(defineProps<{
    disabled?: boolean;
    readonly?: boolean;
    decrementDisabled?: boolean;
    incrementDisabled?: boolean;
    size?: 'sm' | 'md' | 'lg';
    fluid?: boolean;
    inputId?: string;
    ariaLabel?: string;
}>(), {
    disabled: false,
    readonly: false,
    decrementDisabled: false,
    incrementDisabled: false,
    size: 'md',
    fluid: true,
    inputId: undefined,
    ariaLabel: undefined
});

const emit = defineEmits<{
    (e: 'step', direction: -1 | 1): void;
}>();
const { $t } = useLocalization();
const affixContext = inject(inputGroupAffixKey, null);
const affixOwner = Symbol('number-field');

const root = shallowRef<HTMLElement | null>(null);
let releaseAffixes: (() => void) | undefined;

onMounted(() =>
{
    // Pola w teleportowanym popupie nie należą do obrysu wejściowej grupy
    if (affixContext && root.value && affixContext.contains(root.value))
        releaseAffixes = affixContext.claim(affixOwner);
});
onBeforeUnmount(() => releaseAffixes?.());

const inheritedTextAffix = computed(() => affixContext?.owner.value === affixOwner
    && (affixContext.hasPrepend.value || affixContext.hasAppend.value));

function InputGroupPrepend()
{
    return affixContext?.prepend();
}

function InputGroupAppend()
{
    return affixContext?.append();
}

const numberInputClasses = '[&>input]:!h-[28px] [&>input]:min-w-0 [&>input]:flex-1 [&>input]:!rounded-none [&>input]:!border-0 [&>input]:!bg-transparent [&>input]:!px-[4px] [&>input]:text-center [&>input]:tabular-nums [&>input]:!shadow-none [&>input]:!ring-0 [&>input]:[appearance:textfield] [&>input::-webkit-inner-spin-button]:appearance-none [&>input::-webkit-outer-spin-button]:appearance-none';
const buttonClasses = 'flex h-full w-[28px] shrink-0 items-center justify-center border-0 bg-transparent p-0 text-fg-muted cursor-pointer [transition:background_0.12s_ease,color_0.12s_ease] enabled:hover:bg-hover enabled:hover:text-fg enabled:active:bg-border disabled:cursor-not-allowed disabled:bg-transparent disabled:text-field-disabled-icon motion-reduce:transition-none dark:text-fg-muted-dark dark:enabled:hover:bg-hover-dark dark:enabled:hover:text-fg-dark dark:enabled:active:bg-border-dark dark:disabled:text-field-disabled-icon-dark';

function label(direction: -1 | 1)
{
    const text = direction === -1 ? $t('[[[Zmniejsz]]]') : $t('[[[Zwiększ]]]');

    return props.ariaLabel ? `${text}: ${props.ariaLabel}` : text;
}
</script>

<template>
    <span
        ref="root"
        data-number-field
        class="inline-flex h-[30px] min-w-0 items-stretch rounded-[8px] border border-border-strong bg-transparent text-ui-body [transition:border-color_0.12s,box-shadow_0.12s] [&:has(input:enabled:not([readonly])):hover]:border-[color-mix(in_srgb,var(--color-accent)_55%,var(--color-border-strong))] focus-within:border-accent focus-within:ring-[3px] focus-within:ring-[color-mix(in_oklch,var(--color-accent)_20%,transparent)] [&:has(input:disabled)]:!border-field-disabled-border [&:has(input:disabled)]:!bg-field-disabled-bg [&:has(input:disabled)>button]:!border-field-disabled-border motion-reduce:transition-none dark:border-border-strong-dark dark:[&:has(input:enabled:not([readonly])):hover]:border-[color-mix(in_srgb,var(--color-accent)_55%,var(--color-border-strong-dark))] dark:[&:has(input:disabled)]:!border-field-disabled-border-dark dark:[&:has(input:disabled)]:!bg-field-disabled-bg-dark dark:[&:has(input:disabled)>button]:!border-field-disabled-border-dark"
        :class="[numberInputClasses, props.fluid ? 'w-full' : 'w-auto', props.size === 'sm' ? 'text-[0.78125rem]' : props.size === 'lg' ? 'text-[0.875rem]' : 'text-ui-body']"
    >
        <button
            type="button"
            tabindex="-1"
            :class="[buttonClasses, 'rounded-s-[7px] border-e border-e-border-strong dark:border-e-border-strong-dark']"
            :disabled="props.disabled || props.readonly || props.decrementDisabled"
            :aria-label="label(-1)"
            :aria-controls="props.inputId"
            data-number-decrease
            @pointerdown.prevent
            @click="emit('step', -1)"
        >
            <IdeoIcon name="minus" :size="13" aria-hidden="true" />
        </button>
        <span class="flex min-w-0 flex-1 items-center justify-center gap-[4px] overflow-hidden" :class="!$slots.value ? numberInputClasses : undefined" data-number-value>
            <IdeoInputGroupText v-if="inheritedTextAffix && affixContext?.hasPrepend.value" embedded class="!px-[8px]">
                <InputGroupPrepend />
            </IdeoInputGroupText>
            <slot v-if="$slots.value" name="value" />
            <slot v-else />
            <IdeoInputGroupText v-if="inheritedTextAffix && affixContext?.hasAppend.value" embedded class="!px-[8px]">
                <InputGroupAppend />
            </IdeoInputGroupText>
        </span>
        <span v-if="$slots.append" class="flex shrink-0 items-center gap-[4px]">
            <slot name="append" />
        </span>
        <button
            type="button"
            tabindex="-1"
            :class="[buttonClasses, 'rounded-e-[7px] border-s border-s-border-strong dark:border-s-border-strong-dark']"
            :disabled="props.disabled || props.readonly || props.incrementDisabled"
            :aria-label="label(1)"
            :aria-controls="props.inputId"
            data-number-increase
            @pointerdown.prevent
            @click="emit('step', 1)"
        >
            <IdeoIcon name="plus" :size="13" aria-hidden="true" />
        </button>
    </span>
</template>
